import { useEffect, useState } from "react";
import { Box, Chip, Link, Typography } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import { sortBy } from "lodash";

import { FusionPartnerBox } from "components/FusionPartnerBox";
import Alert from "components/shared/Alert";
import RightContent from "components/wrappers/RightContent";
import { CUSTOMER_SUPPORT_EMAIL } from "helpers/constants";
import useApp from "hooks/useApp";
import useCatalog from "hooks/useCatalog";
import useCatalogDetail from "hooks/useCatalogDetail";
import { useQuery } from "hooks/useRouter";
import { IDiseaseType } from "types/diseaseType";
import { IWorkflow } from "types/workflow";

import Table from "./Table";

const useStyles = makeStyles((theme: Theme) => ({
  headerLabel: {
    fontSize: 14,
    lineHeight: "20.41px",
    letterSpacing: 0.1,
    fontWeight: 400,
  },
  chipRow: {
    "& > *:nth-child(2)": {
      marginLeft: theme.spacing(1.5),
      marginRight: theme.spacing(1),
    },
  },
  totalCount: { marginLeft: theme.spacing(2), marginTop: theme.spacing(1) },
}));

const MainContent = () => {
  const classes = useStyles();
  const { workflows, diseaseTypes } = useApp();
  const {
    workflow,
    diseaseType,
    setWorkflow,
    setDiseaseType,
    setFoundGenes,
    setOriginFoundGenes,
  } = useCatalogDetail();
  const query = useQuery();
  const {
    customPanels,
    isPartnerIncluded,
    missingTargets,
    setMissingTargets,
    totalQueryGenes,
  } = useCatalog();
  const [alertVisible, setAlertVisible] = useState<boolean>(false);

  useEffect(() => {
    let isMounted = true;

    if (isMounted) {
      /**
       * Update workflow only when it's in a query
       * IF not, please keep old workflow
       */
      if (!!query.get("workflow")) {
        setWorkflow(
          workflows.find(
            (workflow: IWorkflow) =>
              workflow.id === Number(query.get("workflow"))
          )
        );
      }
      if (!!query.get("diseaseType")) {
        setDiseaseType(
          diseaseTypes.find(
            (diseaseType: IDiseaseType) =>
              diseaseType.id === Number(query.get("diseaseType"))
          )
        );
      }
      if (!!query.get("foundGenes")) {
        setFoundGenes(query.get("foundGenes"));
        setOriginFoundGenes(query.get("foundGenes"));
      }
    }
    return () => {
      isMounted = false;
      setMissingTargets([]);
    };
    /* eslint-disable react-hooks/exhaustive-deps */
  }, []);

  useEffect(() => {
    const oldCountOfCustomPanels = Number(
      localStorage.getItem("cntOfCustomPanels")
    );

    if (
      customPanels.length > 0 &&
      oldCountOfCustomPanels < customPanels.length
    ) {
      setAlertVisible(true);
    }

    localStorage.setItem("cntOfCustomPanels", customPanels.length.toString());
  }, [customPanels]);

  return (
    <RightContent>
      <section>
        <Box display="flex" alignItems="center">
          <Typography variant="h1">Evaluate Panel Options</Typography>
          <Typography
            variant="body1"
            color="textSecondary"
            className={classes.totalCount}
          >
            {totalQueryGenes} query {totalQueryGenes > 1 ? "genes" : "gene"}
          </Typography>
        </Box>
        <Box
          my={3}
          display="flex"
          alignItems="center"
          className={classes.chipRow}
        >
          <Typography
            variant="subtitle2"
            component="h2"
            color="textSecondary"
            className={classes.headerLabel}
          >
            Workflow and Disease Type:
          </Typography>
          {workflow && <Chip label={workflow.name} disabled />}
          {diseaseType && <Chip label={diseaseType.name} disabled />}
        </Box>
        {!!alertVisible && (
          <Box mb={3}>
            <Alert
              severity="info"
              icon={false}
              onClose={() => setAlertVisible(false)}
            >
              Your custom design has been added to the top of the list.
            </Alert>
          </Box>
        )}
        {!!isPartnerIncluded && (
          <Box mb={3}>
            <FusionPartnerBox />
          </Box>
        )}
        {missingTargets.length > 0 && (
          <Box mb={3}>
            <Alert
              severity="info"
              icon={false}
              onDismiss={() => setMissingTargets([])}
            >
              <Typography component="p">
                No predesigned content is available for the following target(s):{" "}
                {sortBy(missingTargets).join(", ")}.
              </Typography>
              <Typography component="p">
                <Link
                  color="primary"
                  href={`mailto:${CUSTOMER_SUPPORT_EMAIL}?subject=Request a consultation`}
                  target="_blank"
                >
                  Request a consultation
                </Link>{" "}
                with the Custom Assay Design team.
              </Typography>
            </Alert>
          </Box>
        )}
        <Table />
      </section>
    </RightContent>
  );
};

export default MainContent;
